import { useCallback, useEffect, useState } from 'react';
import useAuth from './useAuth';
import { getCiudadOptions } from '../services/userService';

const cityCache = {
  data: null,
  promise: null,
};

const normalizeCatalog = (raw) => {
  const base = {
    citiesByDepartment: {},
    cityMap: {},
    allCities: [],
  };

  if (!Array.isArray(raw)) {
    return base;
  }

  raw.forEach((group) => {
    if (!group || typeof group !== 'object') {
      return;
    }
    const { departamento, ciudades } = group;
    if (typeof departamento !== 'string' || departamento.length === 0) {
      return;
    }

    if (!Array.isArray(ciudades) || ciudades.length === 0) {
      base.citiesByDepartment[departamento] = [];
      return;
    }

    const normalizedCities = ciudades
      .map((ciudad) => {
        if (!ciudad || typeof ciudad !== 'object') {
          return null;
        }
        const { id, nombre } = ciudad;
        if (typeof id !== 'string' || id.length === 0) {
          return null;
        }
        const label = typeof nombre === 'string' && nombre.length > 0 ? nombre : id;
        base.cityMap[id] = label;
        const option = { value: id, label };
        base.allCities.push(option);
        return option;
      })
      .filter(Boolean);

    base.citiesByDepartment[departamento] = normalizedCities;
  });

  return base;
};

const fetchWithCache = async (authFetch) => {
  if (!authFetch) {
    return {
      citiesByDepartment: {},
      cityMap: {},
      allCities: [],
    };
  }

  if (cityCache.data) {
    return cityCache.data;
  }

  if (!cityCache.promise) {
    cityCache.promise = getCiudadOptions(authFetch)
      .then((data) => {
        const normalized = normalizeCatalog(data);
        cityCache.data = normalized;
        return normalized;
      })
      .finally(() => {
        cityCache.promise = null;
      });
  }

  return cityCache.promise;
};

const useCiudadesCatalog = () => {
  const { authFetch } = useAuth();
  const [catalog, setCatalog] = useState(() => ({
    citiesByDepartment: {},
    cityMap: {},
    allCities: [],
  }));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!authFetch) {
      setCatalog({ citiesByDepartment: {}, cityMap: {}, allCities: [] });
      setLoading(false);
      setError(null);
      return { citiesByDepartment: {}, cityMap: {}, allCities: [] };
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getCiudadOptions(authFetch);
      const normalized = normalizeCatalog(data);
      cityCache.data = normalized;
      setCatalog(normalized);
      setLoading(false);
      return normalized;
    } catch (err) {
      const normalizedError =
        err instanceof Error
          ? err
          : new Error('No fue posible cargar el catálogo de ciudades.');
      cityCache.data = null;
      setCatalog({ citiesByDepartment: {}, cityMap: {}, allCities: [] });
      setLoading(false);
      setError(normalizedError);
      throw normalizedError;
    }
  }, [authFetch]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!authFetch) {
        if (active) {
          setCatalog({ citiesByDepartment: {}, cityMap: {}, allCities: [] });
          setLoading(false);
          setError(null);
        }
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = await fetchWithCache(authFetch);
        if (!active) {
          return;
        }
        setCatalog(data);
        setLoading(false);
      } catch (err) {
        if (!active) {
          return;
        }
        const normalizedError =
          err instanceof Error
            ? err
            : new Error('No fue posible cargar el catálogo de ciudades.');
        setCatalog({ citiesByDepartment: {}, cityMap: {}, allCities: [] });
        setLoading(false);
        setError(normalizedError);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [authFetch]);

  const getCitiesForDepartment = useCallback(
    (departamento) => {
      const fallback = catalog.allCities ?? [];
      if (!departamento) {
        return fallback;
      }
      return catalog.citiesByDepartment[departamento] ?? fallback;
    },
    [catalog],
  );

  return {
    catalog,
    getCitiesForDepartment,
    cityMap: catalog.cityMap,
    loading,
    error,
    refresh,
  };
};

export default useCiudadesCatalog;
